import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type ThemeOption = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: ThemeOption;
  setTheme: (theme: ThemeOption) => void;
  currentTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState<ThemeOption>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('theme') as ThemeOption) || 'system';
    }
    return 'system';
  });

  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>(() => {
    if (theme === 'dark') return 'dark';
    if (theme === 'light') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  });

  // Apply theme to document
  const applyTheme = (isDark: boolean) => {
    const root = document.documentElement;
    root.setAttribute('data-theme', isDark ? 'dark' : 'light');
    setCurrentTheme(isDark ? 'dark' : 'light');
  };

  // Handle theme changes
  useEffect(() => {
    if (theme === 'system') {
      // Remove stored theme
      localStorage.removeItem('theme');

      // Listen to system preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e: MediaQueryListEvent) => {
        applyTheme(e.matches);
      };

      // Initial application
      applyTheme(mediaQuery.matches);

      // Add listener
      mediaQuery.addEventListener('change', handleChange);

      // Cleanup
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Store and apply manual theme selection
      localStorage.setItem('theme', theme);
      applyTheme(theme === 'dark');

      // Remove system preference listener if it exists
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.removeEventListener('change', () => {});
    }
  }, [theme]);

  const contextValue = useMemo(
    () => ({
      theme,
      setTheme,
      currentTheme,
    }),
    [theme, currentTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
